import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import {
  EmployeeTimeOffArgs,
  ReportDownloadArgs,
  ReportGeneratedArgs,
  SortOptions,
  TimeOffCode,
  TimeOffReportArgs
} from '../../../../models/reports';
import { parseDatePickerValue } from '../../../../utils/datepicker-value-parser';
import { ModalStates } from '../../../../models/modal';
import moment from 'moment';
import { downloadFile } from '../../../../tools/reports-tools';
import { ReportsService } from '../../../../services/reports.service';
import { CompaniesService } from '../../../../services/companies.service';
import { forkJoin } from 'rxjs';
import {
  MultiSelectOption,
  Option
} from '../../../../components/multiselect/multiselect.component';

@Component({
  selector: 'lib-employee-time-off-report',
  templateUrl: './time-off-report.component.html',
  styleUrls: ['./time-off-report.component.css'],
  standalone: false
})
export class EmployeeTimeOffReportComponent implements OnInit {
  @Input()
  public clientCode: string;
  @Output()
  public reportGenerated = new EventEmitter<ReportGeneratedArgs>();
  @Output()
  public reportDownloadClicked = new EventEmitter<ReportDownloadArgs>();
  public reportGenerationForm: UntypedFormGroup;
  public timeOffCodes: Option[];
  public sortOptions: SortOptions[];
  public secondSortOptions: string[] = null;
  private timeOffCodeRespnse: TimeOffCode[];
  private readonly ENDPOINT = 'TimeOff';
  constructor(
    private fb: UntypedFormBuilder,
    private companiesService: CompaniesService,
    private reportsService: ReportsService
  ) {}

  ngOnInit(): void {
    const timeOffCodesObs = this.companiesService.getTimeOffCodes(this.clientCode);
    const sortOptionsObs = this.reportsService.getSortOptions(this.clientCode, this.ENDPOINT);
    forkJoin([timeOffCodesObs, sortOptionsObs]).subscribe(results => {
      this.timeOffCodeRespnse = results[0].TimeOffPlans;
      this.timeOffCodes = results[0].TimeOffPlans.map(x => {
        return {
          value: x.Code,
          text: x.Code
        };
      }).sort((a, b) => {
        if (a.text < b.text) return -1;
        if (a.text > b.text) return 1;
        return 0;
      });
      this.sortOptions = results[1];
      const defaultFirstSortOpt = this.sortOptions.some(x => x.FirstSort === 'Employee Number')
        ? 'Employee Number'
        : null;
      let disableSecondSort = true;
      if (defaultFirstSortOpt != null) {
        const opt = this.sortOptions.find(x => x.FirstSort === defaultFirstSortOpt);
        if (opt.SecondSortOptions != null && opt.SecondSortOptions.length > 0) {
          this.secondSortOptions = opt.SecondSortOptions;
          disableSecondSort = false;
        }
      }

      this.reportGenerationForm = this.fb.group(
        {
          timeOffCodes: this.fb.control(
            this.timeOffCodeRespnse.filter(x => x.Selected).map(x => x.Code)
          ),
          firstSortBy: this.fb.control(defaultFirstSortOpt),
          secondSortBy: this.fb.control({ value: null, disabled: disableSecondSort }),
          excludeTerminatedEmployees: this.fb.control(
            results[0].ExcludeTerminatedEmployeesDefaultValue
          ),
          includeNonActiveParticipatingEmployees: this.fb.control(
            results[0].IncludeNonActiveParticipatingEmployeesDefaultValue
          )
        },
        { validators: [MinimumOneCodeSelected] }
      );
      this.reportGenerationForm.get('firstSortBy').valueChanges.subscribe(val => {
        this.reportGenerationForm.get('secondSortBy').setValue(null);
        const option = this.sortOptions.find(x => x.FirstSort === val);
        if (option.SecondSortOptions == null || option.SecondSortOptions.length === 0) {
          this.secondSortOptions = null;
          this.reportGenerationForm.get('secondSortBy').disable();
        } else {
          this.secondSortOptions = option.SecondSortOptions;
          this.reportGenerationForm.get('secondSortBy').enable();
        }
      });
    });
  }

  public generateReport() {
    const args = {
      args: this._getModel(),
      endpoint: this.ENDPOINT
    };
    this.reportGenerated.emit(args);
  }

  public downloadReport(format: string) {
    const now = moment().format('MMDDYYYY');
    const args = {
      args: this._getModel(),
      endpoint: this.ENDPOINT,
      filename: `Time Off Report_${this.clientCode}_${now}.${format}`,
      format
    };
    this.reportDownloadClicked.emit(args);
  }

  private _getModel() {
    const args: EmployeeTimeOffArgs = {
      TimeOffCodes: this.reportGenerationForm.get('timeOffCodes').value,
      FirstSortBy: this.reportGenerationForm.get('firstSortBy').value,
      SecondSortBy: this.reportGenerationForm.get('secondSortBy').value,
      ExcludeTerminatedEmployees: this.reportGenerationForm.get('excludeTerminatedEmployees').value,
      IncludeNonActiveParticipatingEmployees: this.reportGenerationForm.get(
        'includeNonActiveParticipatingEmployees'
      ).value
    };
    return args;
  }
}

export const MinimumOneCodeSelected: ValidatorFn = (form: AbstractControl) => {
  const field = form.get('timeOffCodes').value;
  if (field.length == 0) {
    return { mustSelectCode: true };
  }
  return null;
};
